import { httpsCallable } from 'firebase/functions';
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from '../utils/objectUtils';
import { toSubmittedAtDate } from '../utils/dateFormatter';
import { functions } from '.';
import logger from '../utils/logger';
import { getErrorMessage } from '../utils/errorUtils';

// deliberately short compared to the rest of this folder. the whole point of moving these writes off
// the browser is that the customer gets a definite outcome instead of an unbounded client write
// queue, and the callable keeps a warm instance and normally answers in a couple of seconds.
//
// MUST stay above `createleadaccountrecords`'s own `timeoutSeconds` (20s), for the same reason as the two
// callables below. This one is retried, so a server budget longer than this leaves the abandoned execution
// running alongside the retry - and while the lead and the conversion event are safe under that (both sit
// behind one `runTransaction`, so exactly one execution sees `isCreated`) and the form DOCUMENTS are safe
// (the id is derived from leadId+surveyId), `Form.create` also read-modify-writes the `forms` array on the
// user AND lead documents. Two overlapping executions can each read a short array and lose an entry - and
// that array is what builds the encounter.
const CREATE_LEAD_ACCOUNT_RECORDS_TIMEOUT = 30000;

// only transport failures are retried - a request that timed out may well have completed on the
// server, and the call is idempotent on leadId so replaying it cannot duplicate the lead, the forms
// or the conversion event. a permission or validation failure would fail the same way twice
const RETRYABLE_CALLABLE_CODES = ['functions/deadline-exceeded', 'functions/unavailable', 'functions/aborted', 'functions/cancelled'];

export const createLeadAccountRecords = async (payload) => {
    if (isObjectNotEmpty(payload)) {
        const func = httpsCallable(functions, 'createleadaccountrecords', { timeout: CREATE_LEAD_ACCOUNT_RECORDS_TIMEOUT });

        try {
            const result = await func({ ...payload });

            return result?.data;
        } catch (error) {
            if (isStringNotEmpty(error?.code) && RETRYABLE_CALLABLE_CODES.includes(error.code)) {
                const result = await func({ ...payload });

                return result?.data;
            }

            throw error;
        }
    }

    return null;
};

// MUST stay above `savefunnelform`'s own `timeoutSeconds`, and the same holds for the progress pair below.
// Both callables are 20s against 30s here.
//
// A server budget LONGER than the client's leaves the abandoned execution alive after the client has given
// up: on the form path it can then land its stale, shorter answer set over a completion that arrived in
// between, and because `submittedAt` is deliberately omitted from the merge, the completion stamp survives
// the regression. On the progress path there is no retry, but the per-record chain releases when the client
// gives up, so the next queued write races the orphan the same way. Under the client SDK neither could
// happen - Firestore orders mutations per document and never times out.
//
// The 10s gap is deliberate. The two budgets run off different clocks: the server's starts when Cloud Run
// accepts the request, so DNS, TLS and the upload all come out of the margin on a slow connection. 20s also
// leaves ~11s of work time over a measured 9.2s cold module load.
const SAVE_FUNNEL_FORM_TIMEOUT = 30000;

// The callable answers with the STORED document, so its timestamps cross the boundary as
// `{_seconds, _nanoseconds}` maps - `firebase-functions` encodes them and nothing on this side reverses
// it. That dto goes straight into redux `lead.forms`, and from there into the order, subscription and
// encounter payloads, where `OrderForm` copies `createdAt`/`modifiedAt` verbatim. A stored map then
// degenerates the `orderBy(encounterForms, ['createdAt'])` the consult pages sort by, because lodash
// coerces two maps to the same "[object Object]". A client SDK write returns real
// Timestamps, so restoring here keeps the record shape it always had.
const FORM_DATE_FIELDS = ['submittedAt', 'createdAt', 'modifiedAt'];

// restores the named date fields on a record the callable boundary flattened; the weight recording
// wrapper restores history entries and the snapshot through it too. toSubmittedAtDate, not fromUtcToDate:
// the latter answers null for a shape it cannot parse, and a submittedAt dropped to null removes that
// survey from the doctor-network payload, which filters on `submittedAt !== null`
export const toRestoredDates = (record, dateFields) => {
    if (isObjectEmpty(record)) {
        return record;
    }

    return dateFields.reduce((restored, field) => (isNotNullOrUndefined(restored?.[field])
        ? { ...restored, [field]: toSubmittedAtDate(restored[field]) }
        : restored), { ...record });
};

const toRestoredFormDates = (savedForm) => toRestoredDates(savedForm, FORM_DATE_FIELDS);

/**
 * The funnel saves a survey form at the end of each survey and again on submit. The document id is derived
 * from (leadId, surveyId), so every one of those is an upsert and a retry after a timeout lands on the same
 * form rather than minting a second answer set.
 *
 * `isSubmitted` is the caller's statement that the survey is FINISHED - today, that the visitor answered its
 * last step. The server stamps submittedAt from it and the client never sends a timestamp: only the caller
 * knows whether the save is a step or a completion, and only the server should decide what time it was. A
 * save that is not a completion leaves any existing stamp alone rather than clearing it.
 *
 * Answers null rather than throwing: the funnel steps read the result with
 * `if (isObjectNotEmpty(updatedForm) ...)` and carry on when it is empty, and five of those handlers have no
 * try/catch and set a full-page loader before saving - so a throw would leave the customer on a spinner with
 * no error and no way forward.
 */
export const saveFunnelForm = async (form, leadId, isSubmitted = false) => {
    if (isObjectNotEmpty(form) && isStringNotEmpty(leadId)) {
        const func = httpsCallable(functions, 'savefunnelform', { timeout: SAVE_FUNNEL_FORM_TIMEOUT });
        const payload = { form: { ...form }, leadId: leadId, isSubmitted: isBooleanTrue(isSubmitted) };

        return await enqueueFormWrite(toFormWriteKey(leadId, form?.surveyId), payload, async (sending) => {
            try {
                const result = await func(sending);

                return toRestoredFormDates(result?.data);
            } catch (error) {
                if (isStringNotEmpty(error?.code) && RETRYABLE_CALLABLE_CODES.includes(error.code)) {
                    try {
                        const result = await func(sending);

                        return toRestoredFormDates(result?.data);
                    } catch (retryError) {
                        logger.logErrorEvent('SAVE_FUNNEL_FORM', retryError, getErrorMessage(retryError), true);

                        return null;
                    }
                }

                logger.logErrorEvent('SAVE_FUNNEL_FORM', error, getErrorMessage(error), true);

                return null;
            }
        });
    }

    return null;
};

// the funnel's progress writes. short timeout and no retry: almost no caller awaits this, a failure is
// logged, and the next step's write carries the same field forward - a retry here would only queue behind
// a connection that is already struggling
const SAVE_FUNNEL_PROGRESS_TIMEOUT = 30000;

/**
 * Progress writes to one record are issued in order, and each waits for the one before it.
 *
 * Almost no caller awaits these - `markLeadDuplicate` is the one that does - so without a chain two writes
 * to the same field race. `status` and `lastFunnelStep` are the pair that
 * matters: a visitor who is disqualified, presses Back and is disqualified again issues DISQUALIFIED,
 * then CREATED, then DISQUALIFIED - and if CREATED lands last the lead stays live in the database while
 * the customer is looking at the disqualified screen, and keeps receiving abandoned-cart mail.
 * `lastFunnelStep` has the same shape and decides where a resume link drops them.
 *
 * The queue is per-record, so one customer's slow write never delays another's, and a failed write does
 * not break the chain behind it.
 */
const enqueueByKey = (chains, key, run) => {
    if (isStringEmpty(key)) {
        return run();
    }

    const previous = chains.get(key) ?? Promise.resolve();
    const result = previous.then(run);
    // what the NEXT write waits on is this, not `result`: it settles either way, so a failed write does
    // not cancel the writes queued behind it. the caller still gets `result` and still sees the failure
    const settled = result.then(() => undefined, () => undefined);

    chains.set(key, settled);
    // released once nothing is queued behind it, so a long funnel session does not grow the map
    settled.then(() => {
        if (chains.get(key) === settled) {
            chains.delete(key);
        }
    });

    return result;
};

const progressWriteChains = new Map();

const toProgressWriteKey = (payload) => payload?.leadId ?? payload?.profileUserId ?? payload?.siteVisitId ?? null;

// exported for the weight recording callable, which writes the same user document a profile write does
// and keys its chain on the same user id, so a weight lands after the profile write issued before it
export const enqueueProgressWrite = (key, run) => enqueueByKey(progressWriteChains, key, run);

/**
 * Form saves are queued per FORM DOCUMENT, for the same reason progress writes are queued per record.
 *
 * With the id derived from (leadId, surveyId), every step's save and the completion save land on one
 * document, rather than one ordered Firestore mutation per field. The server read-modify-writes that
 * document to preserve `submittedAt`, so two overlapping saves are a lost update: a stalled earlier save
 * (or its 30-second retry) can land last and write its shorter answers array and a null `submittedAt`
 * over a completed survey. The consult pages filter on `submittedAt !== null` before building the
 * doctor-network payload, so that drops the survey off the clinical record.
 *
 * The funnel really does overlap them: the survey steps advance the UI before awaiting the save, and the
 * radio steps auto-advance on selection with no in-flight guard.
 */
const formWriteChains = new Map();
const pendingFormWrites = new Map();

const toFormWriteKey = (leadId, surveyId) => (isStringNotEmpty(leadId) && isStringNotEmpty(surveyId) ? `${leadId}:${surveyId}` : null);

/**
 * Queued saves for one form COALESCE: a newer save supersedes an older one that has not started yet.
 *
 * Serializing alone would be correct but unbounded - the survey steps advance the UI before awaiting, so a
 * visitor clicking through on a slow connection can stack several saves under one key, and the completion
 * save sits behind a full-page spinner waiting for all of them. Each save carries the WHOLE answers array,
 * so an older queued save has nothing the newer one lacks; dropping it loses nothing and bounds the wait to
 * one in-flight request plus one.
 *
 * A superseded save resolves with the result of the save that replaced it, so its caller still sees the
 * saved form rather than a null.
 */
const enqueueFormWrite = (key, payload, send) => {
    if (isStringEmpty(key)) {
        return send(payload);
    }

    const superseded = pendingFormWrites.get(key);
    if (superseded !== undefined) {
        // the newer answers win, but COMPLETION IS STICKY: a queued completion replaced by a later
        // in-progress save would mean submittedAt is never stamped at all, and the consult payload filters
        // on it - so the survey would silently stop counting as submitted
        superseded.payload = {
            ...payload,
            isSubmitted: isBooleanTrue(payload.isSubmitted) || isBooleanTrue(superseded.payload.isSubmitted)
        };

        return superseded.promise;
    }

    const slot = { payload: payload };
    slot.promise = enqueueByKey(formWriteChains, key, () => {
        pendingFormWrites.delete(key);

        return send(slot.payload);
    });

    pendingFormWrites.set(key, slot);

    return slot.promise;
};

// A refused PART does not reject: `saveFunnelProgress` on the server answers `{ lead: false, ..., errors: [] }`
// so one refusal cannot discard the others in the same call. The consequence is that a caller's own `.catch`
// never fires for a rules refusal, an ownership refusal, a `db.updateFields` false, or a field the allowlist
// dropped - while redux has already been updated optimistically. This is the only place that sees the shape,
// so it is where the refusal is reported. Deliberately NOT surfaced to the customer (owner decision).
const reportRefusedParts = (result) => {
    if (isListNotEmpty(result?.errors)) {
        const detail = result.errors.map((refusal) => `${refusal?.part}: ${refusal?.message}`).join('; ');
        logger.logErrorEvent('SAVE_FUNNEL_PROGRESS_REFUSED', new Error(detail), 'A funnel progress write was refused.', true);
    }

    return result;
};

export const saveFunnelProgress = async (payload) => {
    if (isObjectNotEmpty(payload)) {
        const func = httpsCallable(functions, 'savefunnelprogress', { timeout: SAVE_FUNNEL_PROGRESS_TIMEOUT });

        return await enqueueProgressWrite(toProgressWriteKey(payload), async () => {
            const result = await func({ ...payload });

            return reportRefusedParts(result?.data);
        });
    }

    return null;
};
